// src/pages/superadmin/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Divider,
  alpha,
  Badge,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Storefront as StorefrontIcon,
  People as PeopleIcon,
  ShoppingBag as ShoppingBagIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Brightness1 as Brightness1Icon,
  Verified as VerifiedIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  LocalShipping as LocalShippingIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ManageAccounts as ManageAccountsIcon,
  Report as ReportIcon,
  Backup as BackupIcon,
  Support as SupportIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { BRAND_COLORS } from '../../buyers/components/styles';
import logo from '../../../assets/images.png';

// ============================================
// ENHANCED COLOR SCHEME FOR SUPER ADMIN
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  whatsapp: '#25D366',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

const ADMIN_COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  gold: '#D4AF37',
  purple: '#6A1B9A',
  teal: '#00838F',
};

// ============================================
// SIDEBAR COMPONENT
// ============================================

const Sidebar = ({
  open,
  onClose,
  isMobile,
  selectedTab,
  onTabChange,
  userData,
  notificationsCount = 0,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get admin name from localStorage
  const adminName = localStorage.getItem('admin_name') || 
                    localStorage.getItem('user_name') || 
                    userData?.name || 
                    userData?.display_name ||
                    userData?.full_name ||
                    'Administrator';

  // Get admin role
  const adminRole = localStorage.getItem('admin_role') || 
                    userData?.role || 
                    'Super Admin';

  // State for expanded menu items
  const [expandedMenus, setExpandedMenus] = useState({
    users: false,
    products: false,
    orders: false,
    settings: false,
  });

  // Menu items with paths and sub-items
  const menuItems = [
    {
      id: 'dashboard',
      path: '/rmrdc/Dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      type: 'single',
    },
    {
     
  id: 'users',
  path: '/rmrdc/users',
  label: 'Users',
  icon: <PeopleIcon />,
  type: 'single',  
    },
    {
   id: 'products',
   path: '/rmrdc/products',
   label: 'Products',
   icon: <InventoryIcon />,
  type: 'single',  
    
    },
   
    {
      id: 'suppliers',
      path: '/rmrdc/Dashboard/suppliers',
      label: 'Suppliers',
      icon: <BusinessIcon />,
      type: 'single',
    },
     {
      id: 'orders',
      path: '/rmrdc/orders',
      label: 'Chemical Requests',
      icon: <ShoppingBagIcon />,
      type: 'single',
    
    },
  

    {
      id: 'settings',
       path: '#',
      label: '',
      icon: <SettingsIcon />,
      type: 'single',
    
    },
  ];

  // Sync selected tab with current route
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check if any menu item or child matches the current path
    for (const item of menuItems) {
      if (item.type === 'single' && item.path === currentPath) {
        onTabChange(item.id);
        return;
      }
      if (item.type === 'parent' && item.children) {
        for (const child of item.children) {
          if (child.path === currentPath) {
            onTabChange(child.id);
            // Auto-expand parent
            setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
            return;
          }
        }
      }
    }
  }, [location.pathname]);

  // Toggle menu expansion
  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // ============================================
  // ANIMATED BRAND COMPONENT
  // ============================================

  const AnimatedBrand = () => {
    const [colorIndex, setColorIndex] = useState(0);
    
    const brandColors = [
      '#D4AF37', // Gold
      '#C62828', // Deep Red
      '#2E7D32', // Forest Green
      '#1565C0', // Royal Blue
      '#6A1B9A', // Deep Purple
    ];

    useEffect(() => {
      const interval = setInterval(() => {
        setColorIndex(prev => (prev + 1) % brandColors.length);
      }, 800);
      return () => clearInterval(interval);
    }, []);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '150%',
            height: '150%',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${brandColors[colorIndex]}15, transparent 70%)`,
            animation: 'glowPulse 1.5s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
        
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{
            background: `linear-gradient(135deg, ${ADMIN_COLORS.gold}, ${ADMIN_COLORS.primary})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '0.5px',
            fontSize: '1.2rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          ChemDB
          <Brightness1Icon
            sx={{
              fontSize: 10,
              color: brandColors[colorIndex],
              animation: 'pulse 0.8s ease-in-out infinite',
              filter: `drop-shadow(0 0 12px ${brandColors[colorIndex]}60)`,
              ml: 0.5,
              verticalAlign: 'middle',
            }}
          />
        </Typography>

        <Chip
          label="SUPER ADMIN"
          size="small"
          sx={{
            bgcolor: alpha(ADMIN_COLORS.gold, 0.15),
            color: ADMIN_COLORS.gold,
            fontWeight: 800,
            fontSize: '0.5rem',
            height: 18,
            letterSpacing: '0.5px',
            border: `1px solid ${alpha(ADMIN_COLORS.gold, 0.2)}`,
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />

        <style>
          {`
            @keyframes glowPulse {
              0%, 100% {
                transform: translate(-50%, -50%) scale(0.8);
                opacity: 0.3;
              }
              50% {
                transform: translate(-50%, -50%) scale(1.2);
                opacity: 0.6;
              }
            }
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 0.8;
              }
              50% {
                transform: scale(1.3);
                opacity: 1;
              }
            }
          `}
        </style>
      </Box>
    );
  };

  // ============================================
  // DRAWER CONTENT
  // ============================================

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Brand Section - Premium Admin Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(ADMIN_COLORS.primary, 0.08)}`,
          background: `linear-gradient(135deg, ${alpha(ADMIN_COLORS.primary, 0.02)}, ${alpha(ADMIN_COLORS.gold, 0.02)})`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(ADMIN_COLORS.gold, 0.2)}, transparent 70%)`,
                animation: 'glowPulse 2s ease-in-out infinite',
              },
            }}
          >
            <img 
              src={logo} 
              alt="Logo" 
              style={{ 
                height: 40, 
                borderRadius: 12,
                boxShadow: `0 4px 16px ${alpha(ADMIN_COLORS.primary, 0.15)}`,
                padding: 3,
                background: 'white',
                position: 'relative',
                zIndex: 1,
              }} 
            />
          </Box>
          <AnimatedBrand />
        </Box>
        
        <Chip
          label="v3.0"
          size="small"
          sx={{
            bgcolor: alpha(ADMIN_COLORS.primary, 0.06),
            color: ADMIN_COLORS.primary,
            fontWeight: 600,
            fontSize: '0.55rem',
            height: 20,
          }}
        />
      </Box>

      {/* Admin Profile Card */}
      <Box
        sx={{
          p: 2,
          mx: 1.5,
          my: 1.5,
          background: `linear-gradient(135deg, ${alpha(ADMIN_COLORS.primary, 0.04)}, ${alpha(ADMIN_COLORS.gold, 0.04)})`,
          borderRadius: 3,
          border: `1px solid ${alpha(ADMIN_COLORS.primary, 0.06)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha(ADMIN_COLORS.primary, 0.06)}`,
            borderColor: alpha(ADMIN_COLORS.primary, 0.15),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: `linear-gradient(135deg, ${ADMIN_COLORS.primary}, ${ADMIN_COLORS.gold})`,
              color: 'white',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: `0 4px 16px ${alpha(ADMIN_COLORS.primary, 0.25)}`,
              border: `2px solid ${alpha(ADMIN_COLORS.gold, 0.3)}`,
            }}
          >
            {adminName?.charAt(0)?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1"
              fontWeight={700} 
              noWrap
              sx={{
                color: COLORS.grey[900],
                letterSpacing: '0.2px',
                fontSize: '0.95rem',
              }}
            >
              {adminName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AdminPanelSettingsIcon sx={{ fontSize: 14, color: ADMIN_COLORS.gold }} />
              <Typography 
                variant="body2"
                noWrap
                sx={{
                  color: ADMIN_COLORS.gold,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.3px',
                }}
              >
                {adminRole}
              </Typography>
            </Box>
          </Box>
          <Badge
            badgeContent=" "
            color="success"
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                boxShadow: `0 0 0 2px white, 0 0 12px ${alpha(COLORS.success, 0.4)}`,
                animation: 'statusPulse 2s ease-in-out infinite',
              },
            }}
          />
        </Box>
        
        <style>
          {`
            @keyframes statusPulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.5; transform: scale(0.8); }
            }
          `}
        </style>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ flex: 1, px: 1.5, pt: 0.5, overflow: 'auto' }}>
        {menuItems.map((item) => {
          const isActive = selectedTab === item.id || 
                           (item.children && item.children.some(c => selectedTab === c.id));
          const isExpanded = expandedMenus[item.id];

          // Single menu item
          if (item.type === 'single') {
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={isActive}
                  onClick={() => {
                    navigate(item.path);
                    onTabChange(item.id);
                    if (isMobile) onClose();
                  }}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 1.5,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(ADMIN_COLORS.primary, 0.06),
                      transform: 'translateX(3px)',
                      '& .MuiListItemIcon-root': {
                        color: ADMIN_COLORS.primary,
                      },
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(ADMIN_COLORS.primary, 0.08),
                      '& .MuiListItemIcon-root': {
                        color: ADMIN_COLORS.primary,
                      },
                      '& .MuiListItemText-primary': {
                        color: ADMIN_COLORS.primary,
                        fontWeight: 700,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3.5,
                        height: 24,
                        borderRadius: '0 4px 4px 0',
                        background: `linear-gradient(180deg, ${ADMIN_COLORS.primary}, ${ADMIN_COLORS.gold})`,
                        boxShadow: `0 2px 8px ${alpha(ADMIN_COLORS.primary, 0.3)}`,
                      },
                    },
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 40, 
                      color: isActive ? ADMIN_COLORS.primary : '#6B7280',
                      transition: 'all 0.25s ease',
                      '& .MuiSvgIcon-root': {
                        fontSize: 22,
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? ADMIN_COLORS.primary : '#1F2937',
                      transition: 'all 0.25s ease',
                      letterSpacing: '0.2px',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          }

          // Parent menu item with children
          if (item.type === 'parent') {
            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => toggleMenu(item.id)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      px: 1.5,
                      transition: 'all 0.25s ease',
                      backgroundColor: isActive ? alpha(ADMIN_COLORS.primary, 0.04) : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(ADMIN_COLORS.primary, 0.06),
                        '& .MuiListItemIcon-root': {
                          color: ADMIN_COLORS.primary,
                        },
                      },
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 40, 
                        color: isActive ? ADMIN_COLORS.primary : '#6B7280',
                        '& .MuiSvgIcon-root': {
                          fontSize: 22,
                        },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? ADMIN_COLORS.primary : '#1F2937',
                        letterSpacing: '0.2px',
                      }}
                    />
                    {isExpanded ? <ExpandLessIcon sx={{ color: '#6B7280' }} /> : <ExpandMoreIcon sx={{ color: '#6B7280' }} />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {item.children.map((child) => {
                      const isChildActive = selectedTab === child.id;
                      return (
                        <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            selected={isChildActive}
                            onClick={() => {
                              navigate(child.path);
                              onTabChange(child.id);
                              if (isMobile) onClose();
                            }}
                            sx={{
                              borderRadius: 2,
                              py: 0.75,
                              px: 1.5,
                              transition: 'all 0.25s ease',
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: alpha(ADMIN_COLORS.primary, 0.04),
                                '& .MuiListItemIcon-root': {
                                  color: ADMIN_COLORS.primary,
                                },
                              },
                              '&.Mui-selected': {
                                backgroundColor: alpha(ADMIN_COLORS.primary, 0.06),
                                '& .MuiListItemIcon-root': {
                                  color: ADMIN_COLORS.primary,
                                },
                                '& .MuiListItemText-primary': {
                                  color: ADMIN_COLORS.primary,
                                  fontWeight: 700,
                                },
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 3,
                                  height: 18,
                                  borderRadius: '0 4px 4px 0',
                                  background: ADMIN_COLORS.primary,
                                },
                              },
                              position: 'relative',
                            }}
                          >
                            <ListItemIcon 
                              sx={{ 
                                minWidth: 32, 
                                color: isChildActive ? ADMIN_COLORS.primary : '#9CA3AF',
                                '& .MuiSvgIcon-root': {
                                  fontSize: 18,
                                },
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: '0.8rem',
                                fontWeight: isChildActive ? 600 : 400,
                                color: isChildActive ? ADMIN_COLORS.primary : '#4B5563',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }
          return null;
        })}
      </List>

      <Divider sx={{ opacity: 0.4, mx: 1.5 }} />

      {/* Logout and Footer */}
      <Box sx={{ p: 1.5 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              py: 1,
              px: 1.5,
              transition: 'all 0.25s ease',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: alpha(ADMIN_COLORS.error, 0.06),
                transform: 'translateX(3px)',
                '& .MuiListItemIcon-root': {
                  color: ADMIN_COLORS.error,
                },
                '& .MuiListItemText-primary': {
                  color: ADMIN_COLORS.error,
                },
              },
            }}
          >
            <ListItemIcon 
              sx={{ 
                minWidth: 40, 
                color: '#6B7280',
                transition: 'all 0.25s ease',
                '& .MuiSvgIcon-root': {
                  fontSize: 22,
                },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                color: '#1F2937',
                fontWeight: 600,
                transition: 'all 0.25s ease',
                letterSpacing: '0.2px',
              }}
            />
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: alpha(ADMIN_COLORS.error, 0.04),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: '#9CA3AF',
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
          </ListItemButton>
        </ListItem>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 1.2,
          textAlign: 'center',
          borderTop: `1px solid ${alpha(ADMIN_COLORS.primary, 0.06)}`,
          bgcolor: alpha(ADMIN_COLORS.primary, 0.01),
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#6B7280',
            fontSize: '0.55rem',
            letterSpacing: 0.5,
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          Admin Panel v3.0 • Secure Connection
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: '#9CA3AF',
            fontSize: '0.5rem',
            display: 'block',
            mt: 0.25,
          }}
        >
          © 2026 ChemMart • All Rights Reserved
        </Typography>
      </Box>

      <style>
        {`
          @keyframes activeDotPulse {
            0%, 100% {
              opacity: 1;
              transform: translateY(-50%) scale(1);
            }
            50% {
              opacity: 0.5;
              transform: translateY(-50%) scale(0.7);
            }
          }
        `}
      </style>
    </Box>
  );

  // ============================================
  // DRAWER
  // ============================================

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      sx={{
        width: 280,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          borderRight: `1px solid ${alpha(ADMIN_COLORS.primary, 0.04)}`,
          bgcolor: '#FFFFFF',
          boxShadow: '4px 0 24px rgba(0,0,0,0.02)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;